import { zodResolver } from "@hookform/resolvers/zod";
import { FC } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { getLocalStorage } from "~/lib/helperFuncation/localStorageService";
import { resetPasswordThunk, setIsSetPasswordTrue, setPasswordThunk } from "~/lib/store/slice/loginSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { USER_MOBILE_NUMBER } from "~/utils/constant";
import { setLatestOtpToken } from "~/utils/smkInterseptor";
import { ComponentHeader, InputComponent, LoginFlowButton, LoginResponseErrorComponent } from "./Login";
import { useTranslation } from "react-i18next";

type PropsType = {
  onSuccess?: () => void;
};
interface PasswordInput {
  password: string;
  confirmPassword: string;
}

const DEFAULT_VALUE = {
  password: "",
  confirmPassword: "",
};

const passwordSchema = z
  .object({
    password: z.string().nonempty("Password is required").min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().nonempty("Confirm Password is required").min(6, "Confirm Password must be at least 6 characters"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

const CreatePassword: FC<PropsType> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { loading, error, isForgotPasswordOtpVerified } = useAppSelector((state) => state.login);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordInput>({ mode: "onSubmit", resolver: zodResolver(passwordSchema), defaultValues: DEFAULT_VALUE });
  const dispatch = useAppDispatch();

  const onSubmit: SubmitHandler<PasswordInput> = async (data) => {
    const mobile = getLocalStorage(USER_MOBILE_NUMBER);

    if (!errors.password && !errors.confirmPassword) {
      if (isForgotPasswordOtpVerified) {
        const restRes = await dispatch(resetPasswordThunk({ password: data.password, confirmPassword: data.confirmPassword }));
        if (resetPasswordThunk.fulfilled.match(restRes)) {
          reset();
          setLatestOtpToken("");
          toast.success("Password reset successfully");
        }
      } else {
        const res = await dispatch(setPasswordThunk({ password: data.password, confirmPassword: data.confirmPassword }));
        if (setPasswordThunk.fulfilled.match(res)) {
          reset();
          onSuccess && onSuccess();
          dispatch(setIsSetPasswordTrue());
          toast.success("Password created successfully");
        }
      }
    }
  };

  return (
    <div className="w-full h-full flex flex-col justify-start md:justify-center items-center gap-6 relative">
      <ComponentHeader className="!text-2xl" title={isForgotPasswordOtpVerified ? t("login_page.reset_password") : t("login_page.create_password")} />

      {error && <LoginResponseErrorComponent />}

      <form noValidate className="w-full flex flex-col items-center gap-8" onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full flex flex-col gap-5">
          <div className="w-full space-y-0.5">
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, value } }) => (
                <InputComponent
                  type="password"
                  label={isForgotPasswordOtpVerified ? t("login_page.crt_new_password") : t("login_page.create_password")}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors.password && <p className="text-12 text-red-500 font-inter">{errors.password.message}</p>}
          </div>

          <div className="w-full space-y-0.5">
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field: { onChange, value } }) => (
                <InputComponent
                  type="password"
                  label={isForgotPasswordOtpVerified ? t("login_page.crm_new_password") : t("login_page.crm_password")}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors.confirmPassword && <p className="text-12 text-red-500 font-inter">{errors.confirmPassword.message}</p>}
          </div>
        </div>

        <div>
          <LoginFlowButton text={isForgotPasswordOtpVerified ? t("login_page.reset_password") : t("login_page.create_password")} isIcon={false} />
        </div>
      </form>
    </div>
  );
};

export default CreatePassword;
